import React from "react";
import { GEO_STYLES } from "@/lib/geo";
import type { GeometryStatus } from "@/api/types";

const STATUS_CLASSES: Record<string, string> = {
  // project
  draft: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  scanning: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
  processing: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
  needs_review: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  validated: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  exported: "bg-cyan-500/15 text-cyan-400 border-cyan-500/30",
  archived: "bg-slate-600/15 text-slate-400 border-slate-600/30",
  blocked: "bg-rose-500/15 text-rose-400 border-rose-500/30",
  // room scan
  not_started: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  capturing: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
  uploaded: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  processed: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  failed: "bg-rose-500/15 text-rose-400 border-rose-500/30",
  // stitch / validation
  auto_matched: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  conflict: "bg-rose-500/15 text-rose-400 border-rose-500/30 animate-pulse",
  pending: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  approved: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  rejected: "bg-rose-500/15 text-rose-400 border-rose-500/30",
  queued: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  completed: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
};

const LABELS: Record<string, string> = {
  draft: "Brouillon", scanning: "Scan", processing: "Traitement", needs_review: "À revoir",
  validated: "Validé", exported: "Exporté", archived: "Archivé", blocked: "Bloqué",
  not_started: "Non démarré", capturing: "Capture", uploaded: "Téléversé", processed: "Traité",
  failed: "Échec", auto_matched: "Auto-assemblé", conflict: "Conflit", pending: "En attente",
  approved: "Approuvé", rejected: "Rejeté", queued: "En file", completed: "Terminé",
};

export const StatusBadge: React.FC<{ status: string; testId?: string; className?: string }> = ({
  status, testId, className = "",
}) => {
  const cls = STATUS_CLASSES[status] || "bg-slate-500/15 text-slate-300 border-slate-500/30";
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-mono uppercase tracking-widest font-semibold ${cls} ${className}`}
    >
      {LABELS[status] || status}
    </span>
  );
};

export const GeometryBadge: React.FC<{ status: GeometryStatus; testId?: string }> = ({ status, testId }) => {
  const s = GEO_STYLES[status];
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-mono uppercase tracking-widest font-semibold ${s.badge}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
};

export const DriftBadge: React.FC<{ drift?: { value: number; level: string }; testId?: string }> = ({ drift, testId }) => {
  if (!drift || drift.level === "ok") {
    return (
      <span data-testid={testId} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-0.5 text-[11px] font-mono uppercase tracking-widest font-semibold text-emerald-400">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Dérive OK
      </span>
    );
  }
  const high = drift.level === "high";
  const cls = high ? "border-rose-500/30 bg-rose-500/10 text-rose-400 animate-pulse" : "border-amber-500/30 bg-amber-500/10 text-amber-400";
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-mono uppercase tracking-widest font-semibold ${cls}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${high ? "bg-rose-400" : "bg-amber-400"}`} /> Dérive {Math.round(drift.value * 100)}cm
    </span>
  );
};
